// Writes a contact import. Consent rows and rejection audit rows are stored
// before any lead row. A failed audit write imports nothing.

import {
  assessContactUpload,
  assessManagedClientUpload,
  consentInsertRow,
  type UploadRejection,
  type UploadRequest,
} from "./marketingConsent.ts";

export interface ImportDb {
  from: (table: string) => {
    insert: (rows: unknown) => {
      select?: () => Promise<{ data: unknown; error: { message?: string } | null }>;
      then?: (
        resolve: (value: { data: unknown; error: { message?: string } | null }) => unknown,
      ) => Promise<unknown>;
    } & PromiseLike<{ data: unknown; error: { message?: string } | null }>;
  };
}

export interface ImportCommitResult {
  created: number;
  rejections: UploadRejection[];
  leads: unknown[];
  error?: string;
}

async function inserted(query: {
  select?: () => Promise<{ data: unknown; error: { message?: string } | null }>;
  then?: (resolve: (value: { data: unknown; error: { message?: string } | null }) => unknown) => Promise<unknown>;
} & PromiseLike<{ data: unknown; error: { message?: string } | null }>): Promise<{ data: unknown; error: { message?: string } | null }> {
  if (typeof query.then === "function") {
    return Promise.resolve(query) as Promise<{ data: unknown; error: { message?: string } | null }>;
  }
  throw new Error("insert did not return a result");
}

export interface ImportWriteOptions {
  /**
   * Dashboard imports cannot insert client consent as the signed-in user.
   * When set, this writes those rows (an edge function using the service role).
   */
  insertClientConsent?: (rows: Record<string, unknown>[]) => Promise<{ error: string | null }>;
}

export async function commitContactImport(
  db: ImportDb,
  request: UploadRequest,
  now: Date = new Date(),
  options: ImportWriteOptions = {},
): Promise<ImportCommitResult> {
  if (request.managedCampaign) return commitManagedClientImport(db, request, now, options);
  const assessment = assessContactUpload(request, now);
  if (assessment.rejections.length > 0) {
    const auditRows = assessment.rejections.map((rejection) => ({
      account_user_id: request.accountUserId,
      actor_user_id: request.actorUserId,
      source_path: request.sourcePath,
      reason: rejection.reasons.join(" "),
      phone: rejection.phone,
      row_number: rejection.rowNumber,
      detail: {
        reasons: rejection.reasons,
        managed_campaign: request.managedCampaign === true,
        blocked_list: assessment.blockedList,
      },
    }));
    const audit = await inserted(db.from("contact_upload_audit").insert(auditRows));
    if (audit.error) {
      return {
        created: 0,
        rejections: assessment.rejections,
        leads: [],
        error: "This upload was blocked, and the rejection could not be recorded. Nothing was imported.",
      };
    }
  }

  if (assessment.accepted.length === 0) {
    const reason = assessment.rejections[0]?.reasons[0] ?? "No contact had valid consent.";
    return { created: 0, rejections: assessment.rejections, leads: [], error: reason };
  }

  const consentRows = assessment.accepted.map((item) => consentInsertRow(item.consent, request.actorUserId));
  const consent = await inserted(db.from("marketing_consent_records").insert(consentRows));
  if (consent.error) {
    return {
      created: 0,
      rejections: assessment.rejections,
      leads: [],
      error: consent.error.message || "Consent records could not be stored, so nothing was imported.",
    };
  }

  const leadRows = assessment.accepted.map((item) => item.lead);
  const leadInsert = db.from("leads").insert(leadRows);
  const leads = leadInsert.select ? await leadInsert.select() : await inserted(leadInsert);
  if (leads.error) {
    return {
      created: 0,
      rejections: assessment.rejections,
      leads: [],
      error: leads.error.message || "Contacts could not be imported.",
    };
  }
  const createdRows = Array.isArray(leads.data) ? leads.data : leadRows;
  return {
    created: createdRows.length,
    rejections: assessment.rejections,
    leads: createdRows,
  };
}

async function commitManagedClientImport(
  db: ImportDb,
  request: UploadRequest,
  now: Date,
  options: ImportWriteOptions,
): Promise<ImportCommitResult> {
  const assessment = assessManagedClientUpload(request, request.contactListId ?? "", now);
  if (assessment.rejections.length > 0) {
    const auditRows = assessment.rejections.map((rejection) => ({
      account_user_id: request.accountUserId,
      actor_user_id: request.actorUserId,
      source_path: request.sourcePath,
      reason: rejection.reasons.join(" "),
      phone: rejection.phone,
      row_number: rejection.rowNumber,
      detail: {
        reasons: rejection.reasons,
        managed_campaign: true,
        record_type: "client_list",
        blocked_list: assessment.blockedList,
      },
    }));
    const audit = await inserted(db.from("contact_upload_audit").insert(auditRows));
    if (audit.error) {
      return {
        created: 0,
        rejections: assessment.rejections,
        leads: [],
        error: "This upload was blocked, and the rejection could not be recorded. Nothing was imported.",
      };
    }
  }
  if (assessment.accepted.length === 0) {
    const reason = assessment.rejections[0]?.reasons[0] ?? "No contact had valid client consent.";
    return { created: 0, rejections: assessment.rejections, leads: [], error: reason };
  }
  const consentRows = assessment.accepted.map((item) => item.clientConsent);
  const consent = options.insertClientConsent
    ? await options.insertClientConsent(consentRows)
    : await inserted(db.from("campaign_client_consent_records").insert(consentRows)).then((result) => ({
        error: result.error?.message ?? null,
      }));
  if (consent.error) {
    return {
      created: 0,
      rejections: assessment.rejections,
      leads: [],
      error: consent.error || "Client consent records could not be stored, so nothing was imported.",
    };
  }
  const leadRows = assessment.accepted.map((item) => item.lead);
  const leadInsert = db.from("leads").insert(leadRows);
  const leads = leadInsert.select ? await leadInsert.select() : await inserted(leadInsert);
  if (leads.error) {
    return {
      created: 0,
      rejections: assessment.rejections,
      leads: [],
      error: leads.error.message || "Contacts could not be imported.",
    };
  }
  const createdRows = Array.isArray(leads.data) ? leads.data : leadRows;
  return { created: createdRows.length, rejections: assessment.rejections, leads: createdRows };
}
